import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import { ThemeState } from '@interfaces/financialProduct';
import { MenuItem, TextField } from '@mui/material';
import { useClientContext } from '@pages/client/clientContext';
import { useHouseholdContext } from '@pages/household/householdContext';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { Typography } from 'hephaestus/web';
import { useContext, useEffect } from 'react';

const FETCH_THEMES = gql`
  query fetchThemes($input: FetchThemesInput!) {
    fetchThemes(input: $input) {
      themes {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

const FETCH_AVAILABLE_THEMES = gql`
  query fetchAvailableThemes($input: FetchAvailableThemesInput!) {
    fetchAvailableThemes(input: $input) {
      themes {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

const ThemeSelect = ({
  onChange,
  value,
  label,
  disabled = false,
  readonly = false,
  focused,
  inheritedText,
  suitabilityScore,
}: {
  onChange: (event: any) => void;
  value: string;
  label: string;
  disabled?: boolean;
  readonly?: boolean;
  focused?: boolean;
  inheritedText?: string;
  suitabilityScore?: number;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const clientContext = useClientContext();
  const householdContext = useHouseholdContext();
  const organizationId =
    activeOrganization.id ??
    clientContext?.orgSettings.id ??
    householdContext?.orgSettings.id;
  const doReadAllThemes = usePermission('read:all_themes');

  const { data, loading, error } = useQuery(
    !doReadAllThemes && suitabilityScore !== undefined
      ? FETCH_AVAILABLE_THEMES
      : FETCH_THEMES,
    {
      skip: !organizationId,
      variables: {
        input:
          !doReadAllThemes && suitabilityScore !== undefined
            ? {
                suitabilityScore,
                organizationId,
              }
            : {
                filter: { organizationId, state: ThemeState.ACTIVE },
                pagination: { perPage: 1000 },
              },
      },
    },
  );
  const themes =
    (data?.fetchThemes ?? data?.fetchAvailableThemes)?.themes ?? [];

  if (error) <Typography variant='bodyLarge'>Error</Typography>;

  /* pre-select if there's exactly one theme available */
  useEffect(() => {
    if (themes.length === 1 && value !== themes[0].id) onChange(themes[0].id);
  }, [themes, value, onChange]);

  return (
    <TextField
      select
      focused={focused}
      value={themes.length > 0 ? value : ''}
      label={label}
      fullWidth
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      InputProps={{
        readOnly: readonly,
      }}
    >
      {loading && <MenuItem>...</MenuItem>}
      {themes.map((x: any) => (
        <MenuItem
          key={x.id}
          value={x.id}
        >{`${translateBackend(x.translatedName)}${inheritedText ?? ''}`}</MenuItem>
      ))}
    </TextField>
  );
};

export default ThemeSelect;

export const testables = {
  FETCH_THEMES,
  FETCH_AVAILABLE_THEMES,
};
